import type { AppState } from '@protector/entities/__legacy__/redux.types';
import { ReloadRegion } from '@protector/entities/region/__legacy__/redux.actions';
import type { ReloadRegionData } from '@protector/entities/region/models';
import { selectSelectedSeasons } from 'core/core.reducer';
import { hasRTRPlanEntitlement } from 'entities/company/company.selectors';
import { getSelectedProperty } from 'entities/property/property.selectors';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const compareIds = (first: string, second: string) => {
  if (first < second) return -1;
  if (first > second) return 1;
  return 0;
};

const buildRequestedRegionKey = (rootRegionId: string, seasonIds: string[]) =>
  `${rootRegionId}|${[...seasonIds].sort(compareIds).join(',')}`;

const useLoadRegions = (): void => {
  const dispatch = useDispatch();
  const requestedRegionKey = useRef<string | undefined>(undefined);
  const selectedProperty = useSelector((state: AppState) => getSelectedProperty(state));
  const regionsIsLoaded = useSelector((state: AppState) => state.entities.region.isRegionsLoaded);
  const isLoadingRegions = useSelector((state: AppState) => state.entities.region.isLoading);
  const regionsError = useSelector((state: AppState) => state.entities.region.error);
  const selectedSeasons = useSelector((state: AppState) => selectSelectedSeasons(state.uiState.global));
  const isLoadingLicensingStatus = useSelector((state: AppState) => state.entities.company.isLoadingLicensingStatus);

  const showOnlyNotesFeature = useSelector(hasRTRPlanEntitlement);
  const shouldReloadRegions = selectedProperty && (selectedSeasons.length || showOnlyNotesFeature);

  useEffect(() => {
    if (!shouldReloadRegions || isLoadingLicensingStatus) {
      return;
    }

    const currentRegionKey = buildRequestedRegionKey(selectedProperty.root_region_id, selectedSeasons);
    const selectionChanged = requestedRegionKey.current !== currentRegionKey;
    const regionsAreMissing = !regionsIsLoaded && !isLoadingRegions && !regionsError;

    if (!selectionChanged && !regionsAreMissing) {
      return;
    }

    requestedRegionKey.current = currentRegionKey;
    dispatch(
      ReloadRegion({
        regionId: selectedProperty.root_region_id,
        seasonIds: selectedSeasons
      } as ReloadRegionData)
    );
  }, [
    selectedSeasons,
    selectedProperty,
    dispatch,
    shouldReloadRegions,
    regionsIsLoaded,
    isLoadingRegions,
    regionsError,
    isLoadingLicensingStatus
  ]);
};

export default useLoadRegions;
